import React, { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ClockIcon, CalendarDaysIcon, XMarkIcon } from '@heroicons/react/24/outline'
import {
  generateHourlyTimes,
  getFutureSlotsForDate,
  formatDateKey,
  getPitchSummaries,
  isCenterBookingEnabled
} from '../../utils/centerAvailability'
import CenterAvailabilityCalendar from './CenterAvailabilityCalendar'

const AvailabilityCalendarDialog = ({ open, onClose, center, compact }) => {
  const { t } = useTranslation()

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !center) return null

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl dark:bg-gray-800 sm:p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="availability-calendar-dialog-title"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3
              id="availability-calendar-dialog-title"
              className="truncate text-lg font-bold text-gray-900 dark:text-white"
            >
              {center.name}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('centers.availabilityCalendarTitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <CenterAvailabilityCalendar center={center} compact={compact} showHeading={false} />
      </div>
    </div>
  )
}

/**
 * @param {'card' | 'inline' | 'modal'} variant
 */
const CenterPitchSlots = ({ center, variant = 'card' }) => {
  const { t } = useTranslation()
  const [calendarOpen, setCalendarOpen] = useState(false)

  const sportLabel = (sport) => {
    if (sport === 'calcio') return t('sports.calcio')
    if (sport === 'padel') return t('sports.padel')
    return sport || ''
  }

  const preview = useMemo(() => {
    if (!center) return null
    const now = new Date()
    const todayKey = formatDateKey(now)
    const hourly = generateHourlyTimes(center.openingTime, center.closingTime)
    const slotsToday = getFutureSlotsForDate(todayKey, hourly, now)
    const pitches = getPitchSummaries(center)
    const openingLabel = `${center.openingTime || '08:00'} – ${center.closingTime || '22:00'}`
    return {
      slotsToday,
      pitches,
      openingLabel,
      hourlyCount: hourly.length
    }
  }, [center])

  if (!preview || (preview.pitches.length === 0 && preview.hourlyCount === 0)) {
    return null
  }

  const bookingsEnabled = isCenterBookingEnabled(center)
  const showSlotsSection = bookingsEnabled && preview.hourlyCount > 0

  const openCalendar = () => setCalendarOpen(true)
  const closeCalendar = () => setCalendarOpen(false)

  if (variant === 'inline') {
    return (
      <>
        <div className="mt-2 space-y-2 border-t border-slate-100 pt-2 dark:border-sport-silver/10">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1 space-y-1">
              {preview.pitches.length > 0 && (
                <p className="text-[10px] leading-snug text-slate-600 dark:text-sport-silver/75 sm:text-xs">
                  <span className="font-semibold text-slate-700 dark:text-sport-silver">{t('centers.pitchesLabel')}:</span>{' '}
                  {preview.pitches.map((p, i) => (
                    <span key={p.id || i}>
                      {p.name || t('centers.pitchUnnamed')}
                      {p.sport ? ` (${sportLabel(p.sport)})` : ''}
                      {i < preview.pitches.length - 1 ? ' · ' : ''}
                    </span>
                  ))}
                </p>
              )}
              {showSlotsSection && (
                <p className="text-[10px] text-slate-500 dark:text-sport-silver/65 sm:text-xs">
                  <ClockIcon className="mr-0.5 inline h-3 w-3 align-text-bottom" />
                  <span className="font-medium text-slate-600 dark:text-sport-silver/80">{t('centers.todaySlots')}:</span>{' '}
                  {preview.slotsToday.length > 0
                    ? preview.slotsToday.slice(0, 6).join(', ') +
                      (preview.slotsToday.length > 6 ? ` (+${preview.slotsToday.length - 6})` : '')
                    : t('centers.noSlotsLeftTodayShort')}
                  {' · '}
                  {t('centers.openingHoursShort')}: {preview.openingLabel}
                </p>
              )}
            </div>
            {showSlotsSection && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  openCalendar()
                }}
                className="shrink-0 rounded-full border border-sport-primary/35 bg-sport-primary/10 p-2 text-sport-primary transition hover:bg-sport-primary/20 dark:border-sport-primary/45 dark:bg-sport-primary/15 dark:text-sport-accent"
                aria-label={t('centers.openAvailabilityCalendar')}
                title={t('centers.openAvailabilityCalendar')}
              >
                <CalendarDaysIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
        <AvailabilityCalendarDialog open={calendarOpen} onClose={closeCalendar} center={center} compact />
      </>
    )
  }

  if (variant === 'modal') {
    return (
      <>
        <div className="space-y-4 border-t border-gray-200 pt-4 dark:border-gray-700">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              {t('centers.pitchesAndCourts')}
            </p>
            {preview.pitches.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('centers.noPitchesListed')}</p>
            ) : (
              <ul className="space-y-2">
                {preview.pitches.map((p) => (
                  <li
                    key={p.id || p.name}
                    className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-gray-700/50"
                  >
                    <span className="font-semibold text-gray-900 dark:text-white">{p.name || t('centers.pitchUnnamed')}</span>
                    {p.sport ? (
                      <span className="text-sport-primary">{sportLabel(p.sport)}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {showSlotsSection && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('centers.calendarPopupHint')}</p>
              <button
                type="button"
                onClick={openCalendar}
                className="inline-flex items-center justify-center gap-2 self-start rounded-full border border-sport-primary/35 bg-sport-primary/10 p-2.5 text-sport-primary transition hover:bg-sport-primary/20 dark:border-sport-primary/45 dark:bg-sport-primary/15 dark:text-sport-accent sm:self-end sm:rounded-xl sm:px-4 sm:py-2.5 sm:text-sm sm:font-semibold"
                aria-label={t('centers.openAvailabilityCalendar')}
                title={t('centers.openAvailabilityCalendar')}
              >
                <CalendarDaysIcon className="h-6 w-6" />
                <span className="hidden sm:inline">{t('centers.openAvailabilityCalendar')}</span>
              </button>
            </div>
          )}
        </div>
        <AvailabilityCalendarDialog open={calendarOpen} onClose={closeCalendar} center={center} compact={false} />
      </>
    )
  }

  /* card */
  return (
    <>
      <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('centers.pitchesAndCourts')}
          </p>
          {showSlotsSection && (
            <button
              type="button"
              onClick={openCalendar}
              className="inline-flex items-center gap-2 rounded-full border border-sport-primary/35 bg-sport-primary/10 px-3 py-2 text-sm font-semibold text-sport-primary transition hover:bg-sport-primary/20 dark:border-sport-primary/45 dark:bg-sport-primary/15 dark:text-sport-accent"
              aria-label={t('centers.openAvailabilityCalendar')}
              title={t('centers.openAvailabilityCalendar')}
            >
              <CalendarDaysIcon className="h-5 w-5" />
              <span className="hidden sm:inline">{t('centers.openAvailabilityCalendar')}</span>
            </button>
          )}
        </div>
        {preview.pitches.length > 0 ? (
          <ul className="space-y-1.5">
            {preview.pitches.map((p) => (
              <li key={p.id || p.name} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="font-medium text-gray-900 dark:text-white">{p.name || t('centers.pitchUnnamed')}</span>
                {p.sport ? <span className="text-sport-primary">{sportLabel(p.sport)}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('centers.noPitchesListed')}</p>
        )}
      </div>
      <AvailabilityCalendarDialog open={calendarOpen} onClose={closeCalendar} center={center} compact />
    </>
  )
}

export default CenterPitchSlots
